/**
 * IANA zone for dashboard calendar-day boundaries (dateFrom/dateTo).
 * Must align with order service {@code APP_TIMEZONE} and assistant {@code TIMEZONE} in deployment.
 */
const DEFAULT_APP_TIMEZONE = "Africa/Kigali";

export function getAppTimeZone(): string {
  const configured = process.env.REACT_APP_TIMEZONE?.trim();
  return configured || DEFAULT_APP_TIMEZONE;
}

/** Calendar year / month (1–12) / day for this instant in {@link getAppTimeZone}. */
export function getCalendarYmdInAppTimeZone(date: Date): {
  year: number;
  month: number;
  day: number;
} {
  const timeZone = getAppTimeZone();
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(date);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    if (y && m && d) {
      return {
        year: parseInt(y, 10),
        month: parseInt(m, 10),
        day: parseInt(d, 10)
      };
    }
  } catch {
    // fall through
  }
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate()
  };
}

/**
 * Calendar {@code yyyy-MM-dd} for this instant in {@link getAppTimeZone} (not UTC).
 * Used for dashboard {@code dateFrom}/{@code dateTo} so they match backend LocalDate
 * semantics for the configured business zone.
 */
export function formatCalendarDateInAppTimeZone(
  date: Date | string | number
): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) {
    return new Date().toISOString().slice(0, 10);
  }
  const timeZone = getAppTimeZone();
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(d);
    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    if (y && m && day) {
      return `${y}-${m}-${day}`;
    }
  } catch {
    // Invalid IANA zone or runtime limitation — fall back to UTC date
  }
  return d.toISOString().slice(0, 10);
}
